// Notification service for lawn care reminders

class NotificationService {
  constructor() {
    this.hasPermission = false;
    this.checkPermission();
  }

  async checkPermission() {
    if ('Notification' in window) {
      this.hasPermission = Notification.permission === 'granted';
      return this.hasPermission;
    }
    return false;
  }

  async requestPermission() {
    if ('Notification' in window && Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      this.hasPermission = permission === 'granted';
      return this.hasPermission;
    }
    return this.hasPermission;
  }

  sendNotification(title, options = {}) {
    if (this.hasPermission) {
      const notification = new Notification(title, {
        icon: '/favicon-256.svg',
        badge: '/favicon.ico',
        dir: 'ltr',
        lang: 'en',
        renotify: false,
        requireInteraction: false,
        silent: false,
        tag: 'lawn-care-reminder',
        ...options
      });

      // Auto-close after 8 seconds
      setTimeout(() => {
        notification.close();
      }, 8000);

      return notification;
    }
    return null;
  }

  scheduleSeasonalReminders() {
    const now = new Date();
    const month = now.getMonth();
    const day = now.getDate();

    // Critical timing reminders
    const reminders = [
      // Spring reminders
      { month: 2, day: 15, title: '🌱 Spring Lawn Care', body: 'Time to start spring cleanup and soil temperature monitoring!' },
      { month: 3, day: 1, title: '🚫 Pre-Emergent Alert', body: 'Apply crabgrass pre-emergent when soil hits 50-55°F!' },
      { month: 3, day: 15, title: '🌾 Spring Seeding', body: 'Perfect time for spring overseeding in Massachusetts!' },
      
      // Summer reminders  
      { month: 5, day: 1, title: '🐛 CRITICAL: Grub Prevention', body: 'Apply grub prevention now - most important timing!' },
      { month: 5, day: 15, title: '🌱 Summer Fertilizer', body: 'Second fertilizer application window if using 2-step program' },
      { month: 6, day: 1, title: '🌡️ Heat Stress Warning', body: 'Raise mower height and water deeply during summer heat' },
      
      // Fall reminders (most critical)
      { month: 7, day: 25, title: '🍂 Fall Prep', body: 'Start planning fall overseeding - prime season coming!' },
      { month: 8, day: 1, title: '⭐ PRIME SEASON', body: 'Peak lawn care season! Time for aeration and overseeding.' },
      { month: 8, day: 15, title: '📅 Overseeding Deadline', body: 'Must complete overseeding by October 15th!' },
      { month: 9, day: 1, title: '🍂 Leaf Management', body: 'Start regular leaf removal to prevent grass smothering' },
      { month: 10, day: 1, title: '❄️ Winter Prep', body: 'Final fertilizer and prepare for winter shutdown' },
      
      // Winter reminders
      { month: 11, day: 15, title: '🔧 Equipment Time', body: 'Perfect season for equipment maintenance and planning' },
      { month: 0, day: 15, title: '🧪 Soil Testing', body: 'Send soil samples to UMass Extension if due this year, or visit our Store for soil test kits' }
    ];

    // Check if today matches any reminder
    const todayReminder = reminders.find(r => r.month === month && r.day === day);
    if (todayReminder && this.hasPermission) {
      // Send notification after a small delay
      setTimeout(() => {
        this.sendNotification(todayReminder.title, {
          body: todayReminder.body,
          tag: `reminder-${month}-${day}`
        });
      }, 2000);
    }

    return todayReminder;
  }

  sendCustomReminder(title, body, delayMinutes = 0) {
    if (delayMinutes > 0) {
      setTimeout(() => {
        this.sendNotification(title, { body });
      }, delayMinutes * 60 * 1000);
    } else {
      this.sendNotification(title, { body });
    }
  }

  // Weekly lawn care reminders
  scheduleWeeklyReminder() {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday
    const month = now.getMonth();
    
    // Send Saturday morning reminders during growing season
    if (dayOfWeek === 6 && month >= 2 && month <= 10) {
      const seasonalTips = {
        2: 'Check for winter damage and start spring cleanup',
        3: 'Monitor soil temperature and apply pre-emergent',
        4: 'Peak fertilization and overseeding time',
        5: 'Apply grub prevention and maintain irrigation',
        6: 'Heat stress management and disease monitoring',
        7: 'Plan fall renovations and check for grub damage',
        8: 'Prime aeration and overseeding season',
        9: 'Continue fall care and leaf management',
        10: 'Final fertilizer and winter preparation'
      };

      this.sendNotification('🌱 Weekend Lawn Care Reminder', {
        body: seasonalTips[month] || 'Check your monthly lawn care tasks!',
        tag: 'weekly-reminder'
      });
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;